import React, { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION, BUILD_NUMBER } from '../version';

function FeedbackButton() {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const captureScreenshot = useCallback(async () => {
    try {
      const canvas = await html2canvas(document.body, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#1a1a1a',
        logging: false,
      });
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Screenshot fehlgeschlagen:', err);
      return null;
    }
  }, []);

  const openFeedback = useCallback(async () => {
    setMessage('');
    setSent(false);
    // Hide any existing modal so screenshot is clean
    setModalOpen(false);
    await new Promise(r => setTimeout(r, 150));
    const img = await captureScreenshot();
    setScreenshot(img);
    setModalOpen(true);
  }, [captureScreenshot]);

  const retakeScreenshot = useCallback(async () => {
    setModalOpen(false);
    await new Promise(r => setTimeout(r, 150));
    const img = await captureScreenshot();
    setScreenshot(img);
    setModalOpen(true);
  }, [captureScreenshot]);

  const handleSend = useCallback(async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.sendFeedback({
        screenshot,
        message: message.trim(),
        page: window.location.pathname + window.location.hash,
        userAgent: navigator.userAgent,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        appVersion: APP_VERSION,
        buildNumber: BUILD_NUMBER,
      });
      setSent(true);
      setTimeout(() => {
        setModalOpen(false);
        setSent(false);
      }, 2000);
    } catch (err) {
      console.error('Feedback senden fehlgeschlagen:', err);
    } finally {
      setSending(false);
    }
  }, [message, screenshot]);

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setMessage('');
    setScreenshot(null);
    setSent(false);
  }, []);

  return (
    <>
      {/* Floating button - bottom right */}
      {!modalOpen && (
        <button
          className="feedback-float-btn"
          onClick={openFeedback}
          title="Feedback senden"
        >
          &#128172;
        </button>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal feedback-modal" onClick={e => e.stopPropagation()}>
            <h3>&#128172; Feedback senden</h3>

            {sent ? (
              <div className="feedback-success">
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>&#9989;</div>
                <p>Feedback gesendet! Danke, {user?.username || 'Nutzer'}.</p>
              </div>
            ) : (
              <>
                {/* Screenshot preview */}
                {screenshot && (
                  <div className="feedback-screenshot-wrap">
                    <img
                      src={screenshot}
                      alt="Screenshot"
                      className="feedback-screenshot-img"
                    />
                    <button
                      className="btn btn-secondary btn-sm feedback-retake-btn"
                      onClick={retakeScreenshot}
                    >
                      NEU
                    </button>
                  </div>
                )}

                {/* Message */}
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    className="form-input"
                    rows="4"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Was ist aufgefallen? Fehler, Wunsch, Idee..."
                    autoFocus
                  />
                </div>

                {/* Meta info */}
                <div className="feedback-meta">
                  <span>Nutzer: {user?.username || 'Unbekannt'}</span>
                  <span>Rolle: {user?.role || '-'}</span>
                </div>

                {/* Actions */}
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={handleClose}>
                    Abbrechen
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSend}
                    disabled={sending || !message.trim()}
                  >
                    {sending ? <span className="spinner" /> : 'Senden'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackButton;
